import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { eur, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, ConfirmDelete } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, FileDown, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const CATS = [["najom", "Nájom"], ["mzdy", "Mzdy"], ["naradie", "Náradie"], ["energie", "Energie"], ["marketing", "Marketing"], ["leasing", "Leasing"], ["poistenie", "Poistenie"], ["ine", "Iné"]];
const catLabel = (c) => (CATS.find((x) => x[0] === c) || [c, c])[1];
const emptyExp = { name: "", amount: "", category: "ine", recurring: true, from_month: "", to_month: "", date: "", active: true };

function Row({ l, v, bold, big }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-semibold" : ""} ${big ? "text-lg" : ""}`}>
      <span>{l}</span>
      <span className={`font-mono ${v < 0 ? "text-red-400" : big ? (v >= 0 ? "text-emerald-400" : "") : ""}`}>{eur(v)}</span>
    </div>
  );
}

function ExpTable({ title, rows, onAdd, onEdit, onDel, recurring }) {
  return (
    <div className="card-surface overflow-hidden">
      <div className="flex items-center justify-between border-b border-border p-4">
        <h3 className="font-heading text-base font-bold uppercase">{title}</h3>
        <Button size="sm" className="gap-1" onClick={onAdd} data-testid={`expense-add-${recurring ? "recurring" : "onetime"}`}><Plus className="h-4 w-4" /> Pridať</Button>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">Zatiaľ žiadne výdaje.</div>
      ) : (
        <table className="w-full text-sm"><tbody>
          {rows.map((e) => (
            <tr key={e.id} className="border-b border-border/40" data-testid={`expense-row-${e.id}`}>
              <td className="p-3"><div className="font-medium">{e.name}{recurring && !e.active && <span className="ml-1 rounded bg-muted px-1 text-[10px] text-muted-foreground">vypnuté</span>}</div><div className="text-xs text-muted-foreground">{catLabel(e.category)}{recurring ? ` · od ${e.from_month || "—"}${e.to_month ? ` do ${e.to_month}` : ""}` : ` · ${e.date || ""}`}</div></td>
              <td className="p-3 text-right font-mono">{eur(e.amount)}</td>
              <td className="p-3"><div className="flex justify-end gap-1"><Button variant="ghost" size="icon" onClick={() => onEdit(e)} data-testid={`expense-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button><ConfirmDelete onConfirm={() => onDel(e)} description={`Zmazať „${e.name}"?`}><Button variant="ghost" size="icon" data-testid={`expense-del-${e.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></ConfirmDelete></div></td>
            </tr>
          ))}
        </tbody></table>
      )}
    </div>
  );
}

export default function Finance() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [exp, setExp] = useState(emptyExp);
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === "admin";

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/finance/summary", { params: { month } }); setSum(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [month]);
  const loadExpenses = useCallback(async () => {
    try { const { data } = await api.get("/finance/expenses"); setExpenses(data || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (isAdmin) { loadSummary(); loadExpenses(); } }, [loadSummary, loadExpenses, isAdmin]);

  if (!isAdmin) return <div className="card-surface p-8 text-center text-muted-foreground" data-testid="finance-no-access">Financie sú dostupné len pre správcu servisu.</div>;

  const openNew = (recurring) => { setExp({ ...emptyExp, recurring, from_month: month, date: `${month}-01` }); setDlgOpen(true); };
  const openEdit = (e) => { setExp({ ...e, amount: String(e.amount) }); setDlgOpen(true); };
  const setF = (k, v) => setExp((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!exp.name.trim()) { toast.error("Zadajte názov"); return; }
    const payload = { ...exp, amount: Number(exp.amount || 0) };
    if (exp.recurring) { payload.date = ""; if (!payload.from_month) payload.from_month = month; }
    else { payload.from_month = ""; payload.to_month = ""; if (!payload.date) payload.date = `${month}-01`; }
    setSaving(true);
    try {
      if (exp.id) await api.put(`/finance/expenses/${exp.id}`, payload);
      else await api.post("/finance/expenses", payload);
      toast.success("Výdaj uložený"); setDlgOpen(false); loadExpenses(); loadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (e) => { try { await api.delete(`/finance/expenses/${e.id}`); toast.success("Zmazané"); loadExpenses(); loadSummary(); } catch { /* ignore */ } };
  const downloadPdf = async () => {
    try { const res = await api.get("/finance/summary/pdf", { params: { month }, responseType: "blob" }); const url = URL.createObjectURL(res.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("PDF sa nepodarilo vytvoriť"); }
  };

  const recurring = expenses.filter((e) => e.recurring);
  const onetime = expenses.filter((e) => !e.recurring && (e.date || "").slice(0, 7) === month);
  const costs = sum ? sum.material + sum.expenses_total + (sum.consumables || 0) + (sum.inventory_diff || 0) : 0;
  const netDelta = sum ? sum.net - (sum.previous?.net || 0) : 0;

  return (
    <div>
      <PageHeader title="Financie / Zisk" subtitle="Mesačný prehľad tržieb, nákladov a čistého zisku (bez DPH)"
        action={<div className="flex flex-wrap items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="finance-month" />
          <Button variant="outline" className="gap-2" onClick={downloadPdf} data-testid="finance-pdf"><FileDown className="h-4 w-4" /> PDF</Button>
        </div>} />

      {loading || !sum ? <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="card-surface p-5" data-testid="finance-card-revenue"><div className="text-sm text-muted-foreground">Tržby</div><div className="mt-1 font-heading text-3xl font-bold">{eur(sum.revenue_total)}</div><div className="mt-1 text-xs text-muted-foreground">Faktúry {eur(sum.revenue_invoiced)} · Interné doklady {eur(sum.revenue_internal)}</div></div>
            <div className="card-surface p-5" data-testid="finance-card-costs"><div className="text-sm text-muted-foreground">Náklady</div><div className="mt-1 font-heading text-3xl font-bold">{eur(costs)}</div><div className="mt-1 text-xs text-muted-foreground">Materiál {eur(sum.material)} · Spotreba {eur(sum.consumables || 0)} · Inventúra {eur(sum.inventory_diff || 0)} · Výdaje {eur(sum.expenses_total)}</div></div>
            <div className={`card-surface p-5 ${sum.net >= 0 ? "border-emerald-500/40" : "border-red-500/40"}`} data-testid="finance-card-net"><div className="text-sm text-muted-foreground">Čistý zisk</div><div className={`mt-1 font-heading text-3xl font-bold ${sum.net >= 0 ? "text-emerald-400" : "text-red-400"}`}>{eur(sum.net)}</div><div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">{netDelta >= 0 ? <ArrowUp className="h-3 w-3 text-emerald-400" /> : <ArrowDown className="h-3 w-3 text-red-400" />} {eur(Math.abs(netDelta))} oproti {sum.previous?.month}</div></div>
          </div>

          <div className="card-surface mt-4 p-5" data-testid="finance-breakdown">
            <h3 className="mb-3 font-heading text-lg font-bold uppercase">Rozpis za {month}</h3>
            <div className="space-y-1.5 text-sm">
              <Row l={`Tržby z uhradených faktúr (${sum.invoices_count || 0})`} v={sum.revenue_invoiced} />
              <Row l={`Tržby z interných dokladov (${sum.internal_count || 0})`} v={sum.revenue_internal} />
              <Row l="Tržby spolu (bez DPH)" v={sum.revenue_total} bold />
              <Row l="− Náklad na vyradený materiál (nákupná cena)" v={-sum.material} />
              <Row l="− Výdaje opakujúce" v={-sum.expenses_recurring} />
              <Row l="− Výdaje príležitostné" v={-sum.expenses_onetime} />
              {sum.categories.length > 0 && <div className="pl-4 text-xs text-muted-foreground">{sum.categories.map((c) => `${catLabel(c.category)}: ${eur(c.amount)}`).join(" · ")}</div>}
              <div className="my-2 border-t border-border" />
              <Row l="Spotrebný materiál" v={-(sum.consumables || 0)} />
              <Row l="Inventúrne rozdiely (manko − prebytok)" v={-(sum.inventory_diff || 0)} />
              <Row l="Čistý zisk" v={sum.net} bold big />
              <div className="mt-3 rounded-lg border border-border bg-background/40 p-3 text-xs text-muted-foreground" data-testid="finance-receivables">Neuhradené faktúry vystavené v mesiaci (pohľadávky, nie sú v tržbách): <b className="font-mono text-amber-400">{eur(sum.receivables)}</b> · DPH z dokladov: <b className="font-mono">{eur(sum.vat_collected)}</b></div>
            </div>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <ExpTable title="Opakujúce sa výdaje (mesačné)" rows={recurring} onAdd={() => openNew(true)} onEdit={openEdit} onDel={del} recurring />
            <ExpTable title={`Príležitostné výdaje · ${month}`} rows={onetime} onAdd={() => openNew(false)} onEdit={openEdit} onDel={del} />
          </div>
        </>
      )}

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="sm:max-w-md" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase">{exp.id ? "Upraviť výdaj" : exp.recurring ? "Nový opakujúci sa výdaj" : "Nový príležitostný výdaj"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Názov</Label><Input value={exp.name} onChange={(e) => setF("name", e.target.value)} data-testid="expense-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Suma € (bez DPH)</Label><Input type="number" step="0.01" value={exp.amount} onChange={(e) => setF("amount", e.target.value)} data-testid="expense-amount" /></div>
              <div className="space-y-1.5"><Label>Kategória</Label>
                <Select value={exp.category} onValueChange={(v) => setF("category", v)}><SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger><SelectContent>{CATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            {exp.recurring ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Platí od</Label><Input type="month" value={exp.from_month} onChange={(e) => setF("from_month", e.target.value)} data-testid="expense-from" /></div>
                  <div className="space-y-1.5"><Label>Platí do (voliteľné)</Label><Input type="month" value={exp.to_month} onChange={(e) => setF("to_month", e.target.value)} data-testid="expense-to" /></div>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-2.5"><div className="text-sm font-medium">Aktívny</div><Switch checked={!!exp.active} onCheckedChange={(v) => setF("active", !!v)} data-testid="expense-active" /></div>
              </>
            ) : (
              <div className="space-y-1.5"><Label>Dátum</Label><Input type="date" value={exp.date} onChange={(e) => setF("date", e.target.value)} data-testid="expense-date" /></div>
            )}
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setDlgOpen(false)}>Zrušiť</Button><Button onClick={save} disabled={saving} data-testid="expense-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
